import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  bubbleKindFor,
  bubbleWaitsForPlayer,
  type HydratedStoryPoint,
  type Page,
  type QuestionPage,
  type ResolvedPhoneShell,
  type ThreadMessage,
  type ThreadView,
} from 'magical-trails';
import { gradeAnswer } from 'magical-trails-react';

import {
  advanceMessagePoint,
  answerQuestion,
  markMessagesRead,
} from '../../../store/trail';
import Avatar from '../Avatar';
import {
  cardKindLabel,
  cardTitleFor,
  senderBubblesFor,
  splitIntoBubbles,
  typingDelayMs,
  type SenderBubble,
} from '../messageContent';
import Composer, { type ReplyTarget } from './Composer';
import {
  AttachmentCard,
  HtmlBubble,
  ImageBubble,
  LocationCard,
  MediaBubble,
  PlayerBubble,
  TimeChip,
  TypingBubble,
} from './Bubbles';

export interface Props {
  thread: ThreadView;
  shell: ResolvedPhoneShell;
  points: HydratedStoryPoint[];
  onBack: () => void;
  onOpenPage: (pointCode: string, pageIndex: number) => void;
  onOpenDossier: (pointCode: string) => void;
  onShowOnMap: (pointCode: string) => void;
}

interface Attempt {
  text: string;
}

/** Everything the sender puts on screen for one page, in order. */
type SenderItem =
  | { kind: 'bubble'; bubble: SenderBubble }
  | { kind: 'media'; page: Extract<Page, { type: 'audioPage' | 'videoPage' }> }
  | { kind: 'ask' }
  | { kind: 'card' };

function senderItemsFor(page: Page): SenderItem[] {
  const spoken = senderBubblesFor(page).map((bubble): SenderItem => ({
    kind: 'bubble',
    bubble,
  }));
  switch (bubbleKindFor(page)) {
    case 'audio':
    case 'video':
      return [
        {
          kind: 'media',
          page: page as Extract<Page, { type: 'audioPage' | 'videoPage' }>,
        },
        ...spoken,
      ];
    case 'photoReply':
      return [...spoken, { kind: 'ask' }];
    case 'card':
      return [{ kind: 'card' }];
    default:
      return spoken;
  }
}

function delayFor(item: SenderItem, reducedMotion: boolean): number {
  if (item.kind === 'bubble') return typingDelayMs(item.bubble, reducedMotion);
  return reducedMotion ? 150 : 900;
}

const pageKey = (message: ThreadMessage) =>
  `${message.point.code}:${message.cursor}`;

const TEN_MINUTES = 10 * 60 * 1000;

const ThreadScreen: React.FC<Props> = ({
  thread,
  shell,
  points,
  onBack,
  onOpenPage,
  onOpenDossier,
  onShowOnMap,
}) => {
  const dispatch = useDispatch();
  const reducedMotion = useMemo(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true,
    []
  );

  // How many of each live page's sender items are on screen, keyed by
  // `pointCode:pageIndex`. A page the player has already had in front of
  // them (a question they left unanswered) opens fully shown; only content
  // that is genuinely new gets typed out.
  const [revealed, setRevealed] = useState<Record<string, number>>(() => {
    const seen: Record<string, number> = {};
    for (const message of thread.messages) {
      const page = message.point.pages?.[message.cursor];
      if (page && !message.unread && message.waitingOnPlayer) {
        seen[pageKey(message)] = senderItemsFor(page).length;
      }
    }
    return seen;
  });
  const [attempts, setAttempts] = useState<Record<string, Attempt[]>>({});
  const [chosenTarget, setChosenTarget] = useState<string | null>(null);

  // Reading the thread marks what is in it as read.
  useEffect(() => {
    const unread = thread.messages
      .filter((message) => !message.point.readAt)
      .map((message) => message.point.code);
    if (unread.length > 0) {
      dispatch(
        markMessagesRead({ codes: unread, readAt: new Date().toISOString() })
      );
    }
  }, [thread.messages, dispatch]);

  // The one message currently being "typed": the first, in thread order,
  // whose live page still has something to put on screen. A message that is
  // waiting on the player stops being the track once its ask is fully shown,
  // so several open tasks in one thread don't queue behind each other.
  const track = useMemo(() => {
    for (const message of thread.messages) {
      if (message.state !== 'active') continue;
      if (message.delivery.presentation === 'attachment') continue;
      const page = message.point.pages?.[message.cursor];
      if (!page) continue;
      const items = senderItemsFor(page);
      const shown = revealed[pageKey(message)] ?? 0;
      const waits = bubbleWaitsForPlayer(bubbleKindFor(page));
      if (shown < items.length || !waits) {
        return { message, page, items, shown, waits };
      }
    }
    return null;
  }, [thread.messages, revealed]);

  const trackKey = track ? pageKey(track.message) : null;
  const trackShown = track?.shown ?? 0;
  const trackTotal = track?.items.length ?? 0;
  const trackWaits = track?.waits ?? false;
  const trackCode = track?.message.point.code;
  const nextDelay =
    track && trackShown < trackTotal
      ? delayFor(track.items[trackShown], reducedMotion)
      : null;

  useEffect(() => {
    if (!trackKey || !trackCode) return;
    if (nextDelay === null) {
      // Everything for this page is on screen. A page that doesn't wait on
      // the player hands over to the next one; a short beat keeps two pages
      // from running into each other.
      if (trackWaits) return;
      const timer = setTimeout(
        () => dispatch(advanceMessagePoint({ code: trackCode })),
        reducedMotion ? 0 : 350
      );
      return () => clearTimeout(timer);
    }
    const timer = setTimeout(() => {
      setRevealed((current) => ({
        ...current,
        [trackKey]: (current[trackKey] ?? 0) + 1,
      }));
    }, nextDelay);
    return () => clearTimeout(timer);
  }, [
    trackKey,
    trackCode,
    trackShown,
    nextDelay,
    trackWaits,
    dispatch,
    reducedMotion,
  ]);

  // Who the composer answers: the question the player tapped, else the
  // OLDEST one whose ask is fully on screen. Oldest, because a later
  // question finishing its typing must not swap the target (and clear the
  // box) under someone half-way through answering the earlier one.
  const replyTargets = useMemo(() => {
    const targets: ReplyTarget[] = [];
    for (const message of thread.messages) {
      if (message.state !== 'active') continue;
      if (message.delivery.presentation === 'attachment') continue;
      const page = message.point.pages?.[message.cursor];
      if (!page || page.type !== 'question') continue;
      const shown = revealed[pageKey(message)] ?? 0;
      if (shown < senderItemsFor(page).length) continue;
      targets.push({
        pointCode: message.point.code,
        pageIndex: message.cursor,
        page,
        senderName: message.sender.name,
      });
    }
    return targets;
  }, [thread.messages, revealed]);

  const replyTarget =
    replyTargets.find((target) => target.pointCode === chosenTarget) ??
    replyTargets[0] ??
    null;

  const handleReply = useCallback(
    (target: ReplyTarget, text: string) => {
      const correct = gradeAnswer(target.page, text);
      if (!correct) {
        // Keyed per QUESTION: a message can ask more than one, and the
        // tries at the first must not resurface under the second.
        const key = `${target.pointCode}:${target.pageIndex}`;
        setAttempts((current) => ({
          ...current,
          [key]: [...(current[key] ?? []), { text }],
        }));
        return;
      }
      // Persist the answer BEFORE moving on: the status pass only lets a
      // question page complete once it carries a userAnswer.
      dispatch(
        answerQuestion({
          code: target.pointCode,
          page: target.pageIndex,
          userAnswer: text,
        })
      );
      dispatch(advanceMessagePoint({ code: target.pointCode }));
      setChosenTarget(null);
      // The reply box is about to unmount with focus inside it.
      scrollRef.current?.focus({ preventScroll: true });
    },
    [dispatch]
  );

  // Keep the newest line in view, unless the player has scrolled up to read.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pinnedRef = useRef(true);
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };
  const attemptCount = Object.values(attempts).reduce(
    (sum, list) => sum + list.length,
    0
  );
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el && pinnedRef.current) el.scrollTop = el.scrollHeight;
  }, [thread.messages, revealed, attemptCount, thread.typing]);

  const typingSender =
    track && trackShown < trackTotal ? track.message.sender : thread.typing;

  const pointTitle = (code: string) =>
    points.find((point) => point.code === code);

  let lastStamp = 0;

  return (
    <section className="Phone_screen Phone_thread" aria-label={thread.name}>
      <header className="Phone_appBar">
        <button
          type="button"
          className="Phone_appBarButton"
          onClick={onBack}
          aria-label="Back"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M15 5 8 12l7 7"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <Avatar
          name={thread.name}
          src={thread.avatar}
          group={thread.isGroup}
          size="sm"
        />
        <div className="Phone_appBarTitle">
          <h1>{thread.name}</h1>
          {thread.role && <p>{thread.role}</p>}
          {thread.isGroup && (
            <p>
              {Array.from(
                new Set(thread.messages.map((message) => message.sender.name))
              ).join(', ')}
            </p>
          )}
        </div>
      </header>

      <div
        className="Phone_log"
        data-group={thread.isGroup}
        ref={scrollRef}
        onScroll={onScroll}
        role="log"
        aria-label={`Conversation with ${thread.name}`}
        // Focusable: a keyboard must be able to scroll it, and it is where
        // focus lands when the reply box goes away after a right answer.
        tabIndex={0}
        aria-live="polite"
        aria-relevant="additions"
      >
        {thread.messages.map((message) => {
          const pages = message.point.pages ?? [];
          const showStamp = message.deliveredAt - lastStamp > TEN_MINUTES;
          lastStamp = message.deliveredAt;
          const from = thread.isGroup ? message.sender : undefined;

          const stamp = showStamp ? (
            <TimeChip at={message.deliveredAt} />
          ) : null;

          if (message.delivery.presentation === 'attachment') {
            return (
              <React.Fragment key={message.point.code}>
                {stamp}
                <AttachmentCard
                  from={from}
                  kind="dossier"
                  title={
                    message.delivery.attachmentLabel?.trim() ||
                    message.point.title
                  }
                  detail={
                    pages.length === 1 ? '1 page' : `${pages.length} pages`
                  }
                  done={message.state === 'done'}
                  actionLabel={message.state === 'done' ? 'Open again' : 'Open'}
                  onOpen={() => onOpenDossier(message.point.code)}
                />
                {message.state === 'done' && message.delivery.linksTo && (
                  <LinkedLocation
                    code={message.delivery.linksTo}
                    point={pointTitle(message.delivery.linksTo)}
                    label={shell.apps.find((a) => a.id === 'map')?.label}
                    onShowOnMap={onShowOnMap}
                  />
                )}
              </React.Fragment>
            );
          }

          return (
            <React.Fragment key={message.point.code}>
              {stamp}
              {pages.map((page, index) => {
                if (index > message.cursor) return null;
                const live =
                  index === message.cursor && message.state === 'active';
                const items = senderItemsFor(page);
                const shown = live
                  ? (revealed[`${message.point.code}:${index}`] ?? 0)
                  : items.length;
                const tries = live
                  ? (attempts[`${message.point.code}:${index}`] ?? [])
                  : [];
                return (
                  <PageInThread
                    key={index}
                    page={page}
                    items={items.slice(0, shown)}
                    live={live}
                    askShown={shown >= items.length}
                    from={from}
                    srName={message.sender.name}
                    attempts={tries}
                    scannerLabel={
                      shell.apps.find((a) => a.id === 'scanner')?.label ??
                      shell.themeSpec.scannerName
                    }
                    selected={
                      live &&
                      replyTargets.length > 1 &&
                      replyTarget?.pointCode === message.point.code
                    }
                    onSelect={
                      live &&
                      page.type === 'question' &&
                      replyTargets.length > 1
                        ? () => setChosenTarget(message.point.code)
                        : undefined
                    }
                    onOpen={() => onOpenPage(message.point.code, index)}
                  />
                );
              })}
              {message.state === 'done' && message.delivery.linksTo && (
                <LinkedLocation
                  code={message.delivery.linksTo}
                  point={pointTitle(message.delivery.linksTo)}
                  label={shell.apps.find((a) => a.id === 'map')?.label}
                  onShowOnMap={onShowOnMap}
                />
              )}
            </React.Fragment>
          );
        })}
        {typingSender && (
          <TypingBubble
            from={thread.isGroup ? typingSender : undefined}
            name={typingSender.name}
          />
        )}
      </div>

      <Composer
        target={replyTarget}
        showTargetStrip={replyTargets.length > 1}
        busy={Boolean(typingSender)}
        typingName={typingSender?.name}
        actionAbove={thread.waitingOnPlayer}
        threadName={thread.name}
        onSend={handleReply}
      />
    </section>
  );
};

interface LinkedLocationProps {
  code: string;
  point: HydratedStoryPoint | undefined;
  label: string | undefined;
  onShowOnMap: (code: string) => void;
}

/** "Show on map", once the point it names is really there to show. */
const LinkedLocation: React.FC<LinkedLocationProps> = ({
  code,
  point,
  label,
  onShowOnMap,
}) => {
  if (!point || point.type !== 'location') return null;
  if (point.status === 'hidden') return null;
  return (
    <LocationCard
      title={point.locationPoint?.title?.trim() || point.title}
      address={point.locationPoint?.address ?? undefined}
      image={point.locationPoint?.locationImage ?? undefined}
      actionLabel={`Show on ${label ?? 'the map'}`}
      onOpen={() => onShowOnMap(code)}
    />
  );
};

interface PageInThreadProps {
  page: Page;
  items: SenderItem[];
  live: boolean;
  askShown: boolean;
  from: ThreadMessage['sender'] | undefined;
  srName: string;
  attempts: Attempt[];
  scannerLabel: string;
  selected: boolean;
  onSelect?: () => void;
  onOpen: () => void;
}

const FALLBACK_WRONG = 'That is not it. Have another go.';

const PageInThread: React.FC<PageInThreadProps> = ({
  page,
  items,
  live,
  askShown,
  from,
  srName,
  attempts,
  scannerLabel,
  selected,
  onSelect,
  onOpen,
}) => {
  const kind = bubbleKindFor(page);
  const done = !live;
  const lastBubble = items.map((item) => item.kind).lastIndexOf('bubble');
  return (
    <>
      {items.map((item, index) => {
        // Only the first bubble of a run carries the sender's name.
        const who = index === 0 ? from : undefined;
        if (item.kind === 'bubble') {
          return item.bubble.kind === 'image' ? (
            <ImageBubble
              key={index}
              from={who}
              src={item.bubble.src}
              alt={item.bubble.alt}
            />
          ) : (
            <HtmlBubble
              key={index}
              from={who}
              srName={index === 0 ? srName : undefined}
              html={item.bubble.html}
              selected={selected}
              onSelect={index === lastBubble ? onSelect : undefined}
            />
          );
        }
        if (item.kind === 'media') {
          return <MediaBubble key={index} from={who} page={item.page} />;
        }
        if (item.kind === 'ask') {
          return done ? null : (
            <AttachmentCard
              key={index}
              from={who}
              kind="camera"
              title={scannerLabel}
              detail="Take the photo to reply"
              done={false}
              actionLabel="Open camera"
              onOpen={onOpen}
            />
          );
        }
        return (
          <AttachmentCard
            key={index}
            from={who}
            kind="activity"
            title={cardTitleFor(page)}
            detail={cardKindLabel(page)}
            done={done}
            actionLabel={done ? 'Open again' : 'Open'}
            onOpen={onOpen}
          />
        );
      })}

      {live &&
        askShown &&
        kind === 'reply' &&
        attempts.map((attempt, index) => (
          <React.Fragment key={index}>
            <PlayerBubble text={attempt.text} />
            <HtmlBubble
              srName={srName}
              html={
                (page as QuestionPage).wrongAnswerText?.trim() || FALLBACK_WRONG
              }
            />
          </React.Fragment>
        ))}

      {done && kind === 'reply' && (page as QuestionPage).userAnswer && (
        <PlayerBubble text={(page as QuestionPage).userAnswer as string} />
      )}
      {done && kind === 'photoReply' && <PlayerBubble photo />}
      {done &&
        (kind === 'reply' || kind === 'photoReply') &&
        'completeText' in page &&
        // A paragraph a bubble, like everything else the sender says.
        splitIntoBubbles(page.completeText).map((bubble, index) =>
          bubble.kind === 'html' ? (
            <HtmlBubble
              key={`said-${index}`}
              srName={index === 0 ? srName : undefined}
              html={bubble.html}
            />
          ) : null
        )}
    </>
  );
};

export default ThreadScreen;
